import React, { useMemo, useState } from "react";

import { Button as ButtonMi, ButtonProps, CircularProgress, Tooltip, Box } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import classNames from "classnames";

import useStyles, { StyledButton, CircularProgressStyled } from "./styles";

export interface FormButtonProps extends ButtonProps {
  children?: any;
  disableElevation?: boolean;
  customColor?: Color | StylesProps;
  loading?: boolean;
  loadingColor?: string;
  tooltip?: string;
  /**
   * @property this property can be used when you don't want to show the button's style but need the button's accessibility
   */
  ghost?: boolean;
  debug?: boolean;
}

type ActionButtonProps = {
  color?: string;
  hoverColor?: string;
  activeColor?: string;
  disabledColor?: string;

  textAlign?: string;
  fontFamily?: string;
  fontSize?: string;
  fontStyle?: string;
  fontWeight?: number;
  lineHeight?: string;
  letterSpacing?: string;
};

export type StylesProps = {
  background?: ActionButtonProps;
  text?: ActionButtonProps;
  border?: ActionButtonProps;
};

export enum Color {
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  SECONDARY_DARK = "SECONDARY_DARK",
  SECONDARY_OUTLINED = "SECONDARY_OUTLINED",
  INFO = "INFO",
  TEXT_INFO = "TEXT_INFO",
  SUCCESS = "SUCCESS",
  SUCCESS_OUTLINED = "SUCCESS_OUTLINED",
  WARNING = "WARNING",
  ERROR = "ERROR",
  ERROR_OUTLINED = "ERROR_OUTLINED",
  ERROR_TEXT = "ERROR_TEXT",
  CANCEL = "CANCEL",
  NOT_SELECTED = "NOT_SELECTED",
  DARK = "DARK",
  DEFAULT = "DEFAULT",
  AI_DUMONT = "AI_DUMONT",
  ICON_ACTION = "ICON_ACTION",
  SEND = "SEND",
  GRAY = "GRAY",
  GRAY_600 = "GRAY_600",
  GRAY_600_TEXT = "GRAY_600_TEXT",
  SECONDARY_DARK_OUTLINED = "SECONDARY_DARK_OUTLINED",
  BRAND = "BRAND",
  BRAND_OUTLINED = "BRAND_OUTLINED",
  BRAND_TEXT = "BRAND_TEXT",
  TEXT_INFO_DANGER = "TEXT_INFO_DANGER",
  TEXT_INFO_BLUE = "TEXT_INFO_BLUE",
  CTA_BRAND_COLOR_TEXT = "CTA_BRAND_COLOR_TEXT",
  GRAY_FILLED = "GRAY_FILLED",
  WHITE = "WHITE",
}

export const Colors = {
  PRIMARY: {
    background: {
      color: "#38CFC4",
      hoverColor: "#3FE3D7",
      activeColor: "#31B6AC",
      disabledColor: "#D0D5DD",
    },
  },
  SECONDARY: {
    background: {
      color: "#308CDB",
      hoverColor: "#369CF3",
      activeColor: "#03569E",
      disabledColor: "#D0D5DD",
    },
  },
  SECONDARY_DARK: {
    background: {
      color: "#0055A4",
      hoverColor: "rgba(0, 85, 164, .88)",
      activeColor: "#0055A4",
      disabledColor: "#E4E7EC",
    },
  },
  SECONDARY_OUTLINED: {
    text: {
      color: "#308CDB",
      hoverColor: "#369CF3",
      activeColor: "#03569E",
      disabledColor: "#667085",
    },
    border: {
      color: "#308CDB",
      hoverColor: "#369CF3",
      activeColor: "#03569E",
      disabledColor: "#D0D5DD",
    },
    background: {
      disabledColor: "#D0D5DD",
    },
  },
  SECONDARY_DARK_OUTLINED: {
    text: {
      color: "#0055A4",
      hoverColor: "#03569E",
      activeColor: "#0055A4",
      disabledColor: "#667085",
    },
    border: {
      color: "#0055A4",
      hoverColor: "#03569E",
      activeColor: "#0055A4",
      disabledColor: "#D0D5DD",
    },
    background: {
      disabledColor: "#D0D5DD",
    },
  },
  INFO: {
    background: {
      color: "#EDF5FB",
      hoverColor: "#F6FBFF",
      activeColor: "#E8F5FF",
      disabledColor: "#D0D5DD",
    },
    text: {
      color: "#3F6280",
      hoverColor: "#3F6280",
      activeColor: "#3F6280",
      disabledColor: "#667085",
    },
  },
  TEXT_INFO: {
    text: {
      color: "#232325",
      hoverColor: "#232325",
      activeColor: "#232325",
      disabledColor: "#667085",
    },
  },
  SUCCESS: {
    text: {
      disabledColor: colors.gray400,
    },
    background: {
      // Old version
      // color: "#12B76A",
      // hoverColor: "#6CE9A6",
      // activeColor: "#12B76A",
      // disabledColor: "#D0D5DD",
      color: "#12B76A",
      hoverColor: "#27bd76",
      activeColor: "#2ec17c",
      disabledColor: colors.gray200,
    },
  },
  SUCCESS_OUTLINED: {
    text: {
      color: "#12B76A",
      hoverColor: "#6CE9A6",
      activeColor: "#12B76A",
      disabledColor: "#D0D5DD",
    },
    border: {
      color: "#12B76A",
      hoverColor: "#6CE9A6",
      activeColor: "#12B76A",
      disabledColor: "#D0D5DD",
    },
  },
  ERROR: {
    background: {
      color: "#F04438",
      hoverColor: "#F97066",
      activeColor: "#D92D20",
      disabledColor: "#D0D5DD",
    },
  },
  ERROR_OUTLINED: {
    text: {
      color: "#F04438",
      hoverColor: "#F97066",
      activeColor: "#F04438",
      disabledColor: "#D0D5DD",
    },
    border: {
      color: "#F04438",
      hoverColor: "#F97066",
      activeColor: "#F04438",
      disabledColor: "#D0D5DD",
    },
  },
  ERROR_TEXT: {
    text: {
      color: "#F04438",
      hoverColor: "#F97066",
      activeColor: "#F04438",
      disabledColor: "#D0D5DD",
    },
  },
  WARNING: {
    background: {
      color: "#F79009",
      hoverColor: "#f79009b3",
      activeColor: "#F79009",
      disabledColor: "#D0D5DD",
    },
    text: {
      color: "#fff",
      hoverColor: "#fff",
      activeColor: "#fff",
      disabledColor: "#667085",
    },
  },
  CANCEL: {
    text: {
      color: "#F04438",
      hoverColor: "#F97066",
      activeColor: "#D92D20",
      disabledColor: "#667085",
    },
  },
  DARK: {
    text: {
      color: "#000000",
      hoverColor: "#000000",
      activeColor: "#000000",
      disabledColor: "#D0D5DD",
    },
  },
  SEND: {
    background: {
      color: "#fff",
      hoverColor: "#fff",
      activeColor: "#fff",
      disabledColor: "#fff",
    },
    text: {
      color: "#465869",
      hoverColor: "#465869",
      activeColor: "#465869",
      disabledColor: "#465869",
    },
  },
  NOT_SELECTED: {
    background: {
      color: "#CCDEEE",
      hoverColor: "#CCDEEE",
      activeColor: "#CCDEEE",
      disabledColor: "#D0D5DD",
    },
    text: {
      color: "#465869",
      hoverColor: "#465869",
      activeColor: "#465869",
      disabledColor: "#667085",
    },
  },
  DEFAULT: {
    text: {
      color: "#308CDB",
      hoverColor: "#369CF3",
      activeColor: "#03569E",
      disabledColor: "#667085",
    },
  },
  AI_DUMONT: {
    background: {
      color: "#028380",
      hoverColor: "#086766",
      activeColor: "#003234",
      disabledColor: "#F1F5F9",
    },
    text: {
      color: "#EEFFFC",
      hoverColor: "#EEFFFC",
      activeColor: "#EEFFFC",
      disabledColor: "#CBD5E1",
    },
  },
  ICON_ACTION: {
    background: {
      color: "#F8FAFC",
      hoverColor: "#E2E8F0",
      activeColor: "#CBD5E1",
      disabledColor: "#F1F5F9",
    },
    text: {
      color: "#334155",
      hoverColor: "#475569",
      activeColor: "#020617",
      disabledColor: "#CBD5E1",
    },
  },
  GRAY: {
    text: {
      color: colors.gray600,
      hoverColor: colors.gray600,
      activeColor: colors.gray600,
      disabledColor: colors.gray400,
      textAlign: "center",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "20px",
      letterSpacing: "0.1px",
    },
    background: {
      hoverColor: "rgba(102, 112, 133, 0.12)",
    },
  },
  GRAY_600: {
    text: {
      color: colors.white,
      hoverColor: colors.white,
      activeColor: colors.white,
      disabledColor: colors.gray400,
      textAlign: "center",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "20px",
      letterSpacing: "0.1px",
    },
    background: {
      hoverColor: "#526572",
      color: "#475467",
      activeColor: "#475467",
      disabledColor: "#475467",
    },
  },
  GRAY_600_TEXT: {
    text: {
      color: "#475467",
      hoverColor: "#475467",
      activeColor: "#475467",
      disabledColor: "#475467",
      textAlign: "center",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "20px",
      letterSpacing: "0.1px",
    },
    border: {
      color: "#475467",
      hoverColor: "#475467",
      activeColor: "#475467",
      disabledColor: "#475467",
    },
    background: {
      hoverColor: colors.white,
      color: colors.white,
      activeColor: colors.white,
      disabledColor: colors.white,
    },
  },
  BRAND: {
    text: {
      color: colors.white,
      hoverColor: colors.white,
      activeColor: colors.white,
      disabledColor: colors.white,
    },
    border: {
      color: colors.brandBlue500,
      hoverColor: colors.brandBlue500,
      activeColor: colors.brandBlue500,
      disabledColor: colors.brandBlue500,
    },
    background: {
      color: colors.brandBlue500,
      hoverColor: `${colors.brandBlue500}EB`,
      activeColor: `${colors.brandBlue500}E0`,
      disabledColor: colors.brandBlue300,
    },
  },
  GRAY_FILLED: {
    text: {
      color: colors.white,
      hoverColor: colors.white,
      activeColor: colors.white,
      disabledColor: colors.white,
    },
    background: {
      color: colors.gray600,
      hoverColor: `${colors.gray600}EB`,
      activeColor: `${colors.gray600}E0`,
      disabledColor: colors.gray200,
    },
  },
  BRAND_TEXT: {
    text: {
      color: colors.brandBlue500,
      hoverColor: colors.brandBlue500,
      activeColor: colors.brandBlue500,
      disabledColor: colors.brandBlue500,
    },
    background: {
      color: "transparent",
      disabledColor: colors.gray50,
    },
  },
  BRAND_OUTLINED: {
    text: {
      color: colors.brandBlue500,
      hoverColor: colors.brandBlue500,
      activeColor: colors.brandBlue500,
      disabledColor: colors.brandBlue500,
    },
    border: {
      color: colors.brandBlue500,
      hoverColor: colors.brandBlue500,
      activeColor: colors.brandBlue500,
      disabledColor: colors.brandBlue500,
    },
    background: {
      color: "transparent",
      disabledColor: colors.gray50,
    },
  },
  TEXT_INFO_DANGER: {
    text: {
      color: colors.error500,
      hoverColor: colors.error500,
      activeColor: colors.error500,
      disabledColor: colors.error500,
    },

    background: {
      color: colors.white,
      hoverColor: "rgba(240, 68, 56, 0.08)",
      activeColor: `rgba(240, 68, 56, 0.12)`,
      disabledColor: colors.gray50,
    },
  },
  TEXT_INFO_BLUE: {
    text: {
      color: colors.brandBlue500,
      hoverColor: colors.brandBlue500,
      activeColor: colors.brandBlue500,
      disabledColor: colors.brandBlue500,
    },

    background: {
      color: colors.white,
      hoverColor: "rgba(0, 85, 164, 0.08)",
      activeColor: `rgba(0, 85, 164, 0.12)`,
      disabledColor: colors.gray50,
    },
  },
  CTA_BRAND_COLOR_TEXT: {
    text: {
      color: colors.brandBlue500,
      hoverColor: colors.brandBlue500,
      activeColor: colors.brandBlue500,
      disabledColor: colors.gray400,
    },

    background: {
      color: "transparent",
      hoverColor: "rgba(0, 85, 164, 0.08)",
      activeColor: `rgba(0, 85, 164, 0.12)`,
      disabledColor: "white",
    },
  },
  WHITE: {
    text: {
      color: colors.gray600,
      hoverColor: colors.gray600,
      activeColor: colors.gray600,
      disabledColor: colors.gray400,
    },
  },
};

const Button = (props: FormButtonProps) => {
  const {
    children,
    customColor,
    disableElevation = true,
    className,
    loading,
    disabled,
    tooltip,
    debug,
    loadingColor = "#fff",
    ...rest
  } = props;

  const [openTooltip, setOpenTooltip] = useState<boolean>(false);

  const getColorsByProps = () => {
    if (customColor) {
      if (typeof customColor === "string") {
        return Colors[customColor];
      }

      return customColor;
    }

    return Colors.DEFAULT;
  };

  const colors = useMemo(() => {
    const currentColor = getColorsByProps();
    if (debug) console.log("currentColor", currentColor);
    return currentColor;
  }, [customColor]);

  const classes = useStyles(!rest.ghost ? getColorsByProps() : ({} as StylesProps));

  const renderChildren = () => {
    if (loading)
      return (
        <Box width="100%" display="flex" flexDirection="column" alignItems="center">
          <CircularProgressStyled customColor={loadingColor} size={25} />
        </Box>
      );

    return children;
  };

  return (
    <Tooltip
      open={tooltip ? openTooltip : false}
      title={tooltip || false}
      placement="top"
      PopperProps={{ keepMounted: true }}
    >
      <StyledButton
        disableElevation={disableElevation}
        className={classNames(className, rest.ghost ? classes.ghost : "")}
        disabled={loading || disabled}
        onMouseEnter={() => setOpenTooltip(true)}
        onMouseLeave={() => setOpenTooltip(false)}
        {...colors}
        {...rest}
      >
        {renderChildren()}
      </StyledButton>
    </Tooltip>
  );
};

export default Button;
